import React, { useState } from 'react';
import { Box, Container, Typography, useTheme, useMediaQuery, Button, Menu, MenuItem, IconButton } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ContactBanner, SiteFooter } from './Home';
import MenuIcon from '@mui/icons-material/Menu';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

// Composant de navigation avec onglets
const NavigationBar = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState<null | HTMLElement>(null);
  const [scolariteMenuAnchor, setScolariteMenuAnchor] = useState<null | HTMLElement>(null);

  const handleMobileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMobileMenuAnchor(event.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuAnchor(null);
  };

  const handleScolariteMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setScolariteMenuAnchor(event.currentTarget);
  };

  const handleScolariteMenuClose = () => {
    setScolariteMenuAnchor(null);
  };

  const handleContactsClick = () => {
    const footerElement = document.getElementById('footer');
    if (footerElement) {
      footerElement.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const navigationItems = [
    { label: 'ACCUEIL', path: '/' },
    { label: 'PRESENTATION', path: '/presentation' },
    { 
      label: 'SCOLARITE', 
      path: '/scolarite',
      hasSubmenu: true,
      submenu: [
        { label: 'MATERNELLE', path: '/scolarite' },
        { label: 'CRÈCHE/GARDERIE', path: '/scolarite-maternel' }
      ]
    },
    { label: 'NOS ACTIVITES', path: '/activites' },
    { label: 'BLOG', path: '/blog' },
    { label: 'CONTACTS', path: '#footer', isScroll: true },
    { label: 'CONNEXION', path: '/login' },
  ];

  return (
    <Box 
      position="absolute" 
      sx={{ 
        bgcolor: 'transparent',
        boxShadow: 'none',
        zIndex: 10,
        top: '60px',
        width: '100%'
      }}
    >
      <Container maxWidth="lg">
        <Box sx={{ 
          display: 'flex',
          justifyContent: { xs: 'flex-end', md: 'space-between' },
          alignItems: 'center',
          py: { xs: 1, md: 2 },
          px: { xs: 2, md: 0 },
          minHeight: { xs: 56, md: 64 }
        }}>
          {/* Logo - Masqué sur mobile */}
          <Box sx={{ 
            display: { xs: 'none', md: 'flex' },
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Box sx={{
              width: 60,
              height: 60,
              border: '2px solid #87CEEB',
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'white',
              overflow: 'hidden',
              p: 0.5,
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}>
              <img
                src="/img/pages/logologo.webp"
                alt="La Maison des Enfants La petite Académie"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  display: 'block'
                }}
              />
            </Box>
          </Box>

          {/* Navigation Desktop */}
          {!isMobile && (
            <Box sx={{ 
              display: 'flex', 
              gap: { md: 2, lg: 3 },
              alignItems: 'center'
            }}>
              {navigationItems.map((item) => (
                <Box key={item.label}>
                  {item.hasSubmenu ? (
                    <>
                      <Typography
                        onClick={handleScolariteMenuOpen}
                        sx={{
                          color: 'white',
                          fontWeight: 600,
                          fontSize: { md: 12, lg: 14 },
                          textTransform: 'uppercase',
                          letterSpacing: 0.5,
                          px: { md: 1.5, lg: 2 },
                          py: 1,
                          borderBottom: '2px solid transparent',
                          cursor: 'pointer',
                          '&:hover': {
                            color: '#FF9800',
                            borderBottom: '2px solid #FF9800',
                          }
                        }}
                      >
                        {item.label}
                      </Typography>
                      <Menu
                        anchorEl={scolariteMenuAnchor}
                        open={Boolean(scolariteMenuAnchor)}
                        onClose={handleScolariteMenuClose}
                        sx={{
                          '& .MuiPaper-root': {
                            bgcolor: 'rgba(23, 128, 194, 0.95)',
                            color: 'white',
                            mt: 1,
                            backdropFilter: 'blur(10px)',
                            minWidth: 200
                          }
                        }}
                      >
                        {item.submenu?.map((subItem) => (
                          <MenuItem
                            key={subItem.label}
                            component={RouterLink}
                            to={subItem.path}
                            onClick={handleScolariteMenuClose}
                            sx={{
                              color: 'white',
                              fontWeight: 600,
                              fontSize: 14,
                              textTransform: 'uppercase',
                              '&:hover': {
                                bgcolor: 'rgba(255, 255, 255, 0.1)',
                              }
                            }}
                          >
                            {subItem.label}
                          </MenuItem>
                        ))}
                      </Menu>
                    </>
                  ) : item.isScroll ? (
                    <Typography
                      onClick={handleContactsClick}
                      sx={{
                        color: 'white',
                        fontWeight: 600,
                        fontSize: { md: 12, lg: 14 },
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        px: { md: 1.5, lg: 2 },
                        py: 1,
                        borderBottom: '2px solid transparent',
                        cursor: 'pointer',
                        '&:hover': {
                          color: '#FF9800',
                          borderBottom: '2px solid #FF9800',
                        }
                      }}
                    >
                      {item.label}
                    </Typography>
                  ) : (
                    <Typography
                      component={RouterLink}
                      to={item.path}
                      sx={{
                        color: 'white',
                        fontWeight: 600,
                        fontSize: { md: 12, lg: 14 },
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        px: { md: 1.5, lg: 2 },
                        py: 1,
                        borderBottom: '2px solid transparent',
                        cursor: 'pointer',
                        textDecoration: 'none',
                        '&:hover': {
                          color: '#FF9800',
                          borderBottom: '2px solid #FF9800',
                        }
                      }}
                    >
                      {item.label}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          )}

          {/* Menu Mobile */}
          {isMobile && (
            <>
              <IconButton
                color="inherit"
                onClick={handleMobileMenuOpen}
                sx={{ color: 'white' }}
              >
                <MenuIcon />
              </IconButton>
              <Menu
                anchorEl={mobileMenuAnchor}
                open={Boolean(mobileMenuAnchor)}
                onClose={handleMobileMenuClose}
                sx={{
                  '& .MuiPaper-root': {
                    bgcolor: 'rgba(23, 128, 194, 0.95)',
                    color: 'white',
                    mt: 1,
                    backdropFilter: 'blur(10px)'
                  }
                }}
              >
                {navigationItems.map((item) => (
                  <Box key={item.label}>
                    {item.hasSubmenu ? (
                      <>
                        <MenuItem
                          onClick={handleScolariteMenuOpen}
                          sx={{
                            color: 'white',
                            fontWeight: 600,
                            fontSize: 14,
                            textTransform: 'uppercase',
                            '&:hover': {
                              bgcolor: 'rgba(255, 255, 255, 0.1)',
                            }
                          }}
                        >
                          {item.label}
                          <KeyboardArrowDownIcon sx={{ ml: 0.5, fontSize: 16 }} />
                        </MenuItem>
                        <Menu
                          anchorEl={scolariteMenuAnchor}
                          open={Boolean(scolariteMenuAnchor)}
                          onClose={handleScolariteMenuClose}
                          sx={{
                            '& .MuiPaper-root': {
                              bgcolor: 'rgba(23, 128, 194, 0.95)',
                              color: 'white',
                              mt: 1,
                              backdropFilter: 'blur(10px)',
                              minWidth: 200
                            }
                          }}
                        >
                          {item.submenu?.map((subItem) => (
                            <MenuItem
                              key={subItem.label}
                              component={RouterLink}
                              to={subItem.path}
                              onClick={() => {
                                handleScolariteMenuClose();
                                handleMobileMenuClose();
                              }}
                              sx={{
                                color: 'white',
                                fontWeight: 600,
                                fontSize: 14,
                                textTransform: 'uppercase',
                                '&:hover': {
                                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                                }
                              }}
                            >
                              {subItem.label}
                            </MenuItem>
                          ))}
                        </Menu>
                      </>
                    ) : item.isScroll ? (
                      <MenuItem
                        onClick={() => {
                          handleContactsClick();
                          handleMobileMenuClose();
                        }}
                        sx={{
                          color: 'white',
                          fontWeight: 600,
                          fontSize: 14,
                          textTransform: 'uppercase',
                          '&:hover': {
                            bgcolor: 'rgba(255, 255, 255, 0.1)',
                          }
                        }}
                      >
                        {item.label}
                      </MenuItem>
                    ) : (
                      <MenuItem
                        component={RouterLink}
                        to={item.path}
                        onClick={handleMobileMenuClose}
                        sx={{
                          color: 'white',
                          fontWeight: 600,
                          fontSize: 14,
                          textTransform: 'uppercase',
                          '&:hover': {
                            bgcolor: 'rgba(255, 255, 255, 0.1)',
                          }
                        }}
                      >
                        {item.label}
                      </MenuItem>
                    )}
                  </Box>
                ))}
              </Menu>
            </>
          )}
        </Box>
      </Container>
    </Box>
  );
};

// Section Hero pour les sorties découverte
const SortieHeroSection = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Box sx={{
      position: 'relative',
      width: '100%',
      height: '40vh',
      backgroundImage: 'url(/img/pages/65.jpg)',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      overflow: 'hidden'
    }}>
      {/* Overlay sombre semi-transparent */}
      <Box sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        bgcolor: 'rgba(0, 0, 0, 0.6)',
        zIndex: 1
      }} />

      {/* Contenu principal */}
      <Box sx={{
        position: 'relative',
        zIndex: 3,
        textAlign: 'center',
        color: 'white'
      }}>
        <Typography
          variant="h1"
          sx={{
            color: 'white',
            fontWeight: 'bold',
            fontSize: { xs: 32, md: 48, lg: 64 },
            textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
            mb: 2,
            textTransform: 'uppercase',
            letterSpacing: 2,
            fontFamily: 'Arial, sans-serif'
          }}
        >
          SORTIES DÉCOUVERTE
        </Typography>
        
        <Typography
          variant="h4"
          sx={{
            color: 'white',
            fontWeight: 400,
            fontSize: { xs: 16, md: 20, lg: 24 },
            textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
            fontFamily: 'Arial, sans-serif'
          }}
        >
          Nos sorties éducatives permettent aux enfants d'explorer le monde qui les entoure
        </Typography>
      </Box>
    </Box>
  );
};

// Section principale des sorties découverte
const SortieContentSection = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const sorties = [
    {
      title: "Musée des Civilisations",
      description: "Découverte de l'histoire et des traditions ivoiriennes à travers les objets et œuvres d'art",
      date: "Mensuel",
      age: "4-5 ans",
      duration: "2 heures"
    },
    {
      title: "Parc du Banco",
      description: "Exploration de la nature, observation des animaux et sensibilisation à l'environnement",
      date: "Bimensuel",
      age: "3-5 ans",
      duration: "3 heures"
    },
    {
      title: "Bibliothèque Nationale",
      description: "Découverte des livres, contes et histoires pour développer l'amour de la lecture",
      date: "Mensuel",
      age: "3-5 ans",
      duration: "1h30"
    },
    {
      title: "Marché de Cocody",
      description: "Découverte des fruits et légumes locaux, sensibilisation à l'alimentation saine",
      date: "Trimestriel",
      age: "4-5 ans",
      duration: "2 heures"
    },
    {
      title: "Jardin Botanique",
      description: "Exploration de la diversité végétale et apprentissage du respect de la nature",
      date: "Bimensuel",
      age: "3-5 ans",
      duration: "2h30"
    },
    {
      title: "Centre Culturel",
      description: "Découverte des arts, spectacles et activités culturelles adaptées aux enfants",
      date: "Mensuel",
      age: "4-5 ans",
      duration: "2 heures"
    }
  ];

  return (
    <Box sx={{
      bgcolor: 'white',
      py: { xs: 4, md: 6 },
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Motifs décoratifs en arrière-plan */}
      <Box sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundImage: 'url(/img/pages/doodles.png)',
        backgroundSize: '200px',
        backgroundRepeat: 'repeat',
        opacity: 0.1,
        zIndex: 0
      }} />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
        <Typography
          variant="h2"
          sx={{
            color: '#1780c2',
            fontWeight: 700,
            fontSize: { xs: 24, md: 32, lg: 36 },
            textAlign: 'center',
            mb: 6,
            fontFamily: 'Arial, sans-serif'
          }}
        >
          Sorties éducatives et découverte
        </Typography>

        {/* Grille des sorties */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
          gap: 4,
          mb: 6
        }}>
          {sorties.map((sortie, index) => (
            <Box
              key={index}
              sx={{
                bgcolor: '#f8f9fa',
                p: 3,
                borderRadius: 2,
                border: '2px solid #32CD32',
                textAlign: 'center',
                transition: 'transform 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-5px)',
                  boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
                }
              }}
            >
              <Typography
                variant="h5"
                sx={{
                  color: '#32CD32',
                  fontWeight: 700,
                  fontSize: { xs: 16, md: 18 },
                  mb: 2,
                  fontFamily: 'Arial, sans-serif'
                }}
              >
                {sortie.title}
              </Typography>
              
              <Typography
                sx={{
                  color: '#333',
                  fontSize: { xs: 14, md: 15 },
                  mb: 3,
                  fontFamily: 'Arial, sans-serif'
                }}
              >
                {sortie.description}
              </Typography>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{
                  bgcolor: '#1780c2',
                  color: 'white',
                  p: 1,
                  borderRadius: 1,
                  flex: 1,
                  mr: 1
                }}>
                  <Typography sx={{ fontSize: { xs: 11, md: 12 }, fontWeight: 600 }}>
                    {sortie.date}
                  </Typography>
                </Box>
                
                <Box sx={{
                  bgcolor: '#FF9800',
                  color: 'white',
                  p: 1,
                  borderRadius: 1,
                  flex: 1
                }}>
                  <Typography sx={{ fontSize: { xs: 11, md: 12 }, fontWeight: 600 }}>
                    {sortie.duration}
                  </Typography>
                </Box>
              </Box>
              
              <Typography
                sx={{
                  color: '#666',
                  fontSize: { xs: 12, md: 14 },
                  fontStyle: 'italic'
                }}
              >
                Âge : {sortie.age}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Informations importantes */}
        <Box sx={{
          bgcolor: '#32CD32',
          color: 'white',
          p: 4,
          borderRadius: 2,
          textAlign: 'center'
        }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              fontSize: { xs: 18, md: 22 },
              mb: 3,
              fontFamily: 'Arial, sans-serif'
            }}
          >
            Informations importantes
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: 14, md: 16 },
              mb: 3,
              fontFamily: 'Arial, sans-serif'
            }}
          >
            Toutes les sorties sont encadrées par notre équipe pédagogique qualifiée. 
            Une autorisation parentale est requise pour chaque sortie.
          </Typography>
          <Button
            variant="contained"
            sx={{
              bgcolor: '#FF9800',
              color: 'white',
              fontWeight: 600,
              px: 4,
              py: 1.5,
              '&:hover': {
                bgcolor: '#F57C00',
              }
            }}
          >
            Autoriser mon enfant
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

// Composant principal
const SortieDecouverte = () => {
  return (
    <>
      <Helmet>
        <title>Sorties Découverte - La Petite Académie Abidjan</title>
        <meta name="description" content="Découvrez nos sorties éducatives et découverte pour enfants : musée, parc, bibliothèque, marché, jardin botanique et centre culturel à Abidjan." />
        <meta name="keywords" content="sorties éducatives enfants, découverte Abidjan, musée parc bibliothèque, La Petite Académie, sorties scolaires" />
        <meta property="og:title" content="Sorties Découverte - La Petite Académie Abidjan" />
        <meta property="og:description" content="Nos sorties éducatives permettent aux enfants d'explorer le monde qui les entoure à travers des visites culturelles et naturelles." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://lapetiteacademie.ci/sortie-decouverte" />
        <link rel="canonical" href="https://lapetiteacademie.ci/sortie-decouverte" />
      </Helmet>
      <Box sx={{ 
        width: '100%',
        margin: 0,
        padding: 0
      }}>
        <ContactBanner />
        <NavigationBar />
        <SortieHeroSection />
        <SortieContentSection />
        <SiteFooter />
      </Box>
    </>
  );
};

export default SortieDecouverte;
